import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, XCircle, Clock, Copy, Check, QrCode, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";
import { formatMac, isCompleteMac } from "@/lib/mac";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const brl = (n) => (n != null ? Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "—");

export default function PixCheckout({ app, open, onOpenChange, resellerId, pixProvider }) {
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState("form"); // form | pay | done
  const [form, setForm] = useState({ mac_app: "", key: "", email: "", contact: "", cpf: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [charge, setCharge] = useState(null);
  const [orderStatus, setOrderStatus] = useState(null);
  const [copied, setCopied] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);

  const applyCoupon = async () => {
    if (!coupon.trim()) { setDiscount(0); return; }
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon.trim(), amount: Number(app.price) });
      setDiscount(data.discount); setError("");
    } catch (e) { setDiscount(0); setError(apiError(e)); }
  };
  const pollRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep("form");
      setForm({ mac_app: "", key: "", email: user?.email || "", contact: "", cpf: "" });
      setError("");
      setLoading(false);
      setCharge(null);
      setOrderStatus(null);
      setCopied(false);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, app, user]);

  if (!app) return null;

  const macLabel = app.needs_device_id ? "ID do Dispositivo (Device ID)" : "Endereço MAC";
  const macPlaceholder = app.needs_device_id ? "Ex: 1234567890" : "Ex: 24:FC:E5:0B:03:36";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onMacChange = (e) =>
    setForm((f) => ({ ...f, mac_app: app.needs_device_id ? e.target.value : formatMac(e.target.value) }));

  const validate = () => {
    if (!form.mac_app.trim()) return "Informe o " + macLabel + ".";
    if (!app.needs_device_id && !isCompleteMac(form.mac_app)) return "Informe um MAC válido (ex: 49:78:2d:63:87:d9).";
    if (app.needs_mac_key && !form.key.trim()) return "Informe a Chave (KEY).";
    if (!form.email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email)) return "Informe um e-mail válido.";
    if (!form.contact.trim()) return "Informe seu WhatsApp ou Telegram.";
    if (pixProvider === "asaas" && form.cpf.replace(/\D/g, "").length < 11) return "Informe um CPF válido (obrigatório nesta loja).";
    return "";
  };

  const startPolling = (orderId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const { data } = await api.get(`/pix/order/${orderId}`);
        setOrderStatus(data);
        if (data.status === "activated") {
          clearInterval(pollRef.current);
          if (user) refreshUser();
          setStep("done");
        }
      } catch { /* keep polling */ }
    }, 4000);
  };

  const createCharge = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/pix/checkout", {
        app_id: app.id,
        mac_app: form.mac_app.trim(),
        key: form.key.trim() || null,
        email: form.email.trim(),
        contact: form.contact.trim(),
        reseller_id: resellerId || null,
        coupon_code: coupon.trim() || null,
        cpf: form.cpf.trim() || null,
      });
      setCharge(data);
      setStep("pay");
      startPolling(data.order_id);
    } catch (e) {
      setError(apiError(e));
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(charge?.brcode || "");
      setCopied(true);
      toast.success("Código Pix copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar.");
    }
  };

  const doneView = () => {
    const st = orderStatus?.activation_status;
    if (st === "approved")
      return { icon: <CheckCircle2 className="h-12 w-12 text-green-500" />, title: "Pagamento confirmado e ativado!", desc: "Seu aplicativo foi ativado com sucesso." };
    if (st === "failed")
      return { icon: <XCircle className="h-12 w-12 text-destructive" />, title: "Pagamento confirmado", desc: "Houve um problema na ativação. Nossa equipe irá verificar e resolver." };
    if (st === "refunded")
      return { icon: <RotateCcw className="h-12 w-12 text-amber-500" />, title: "Pagamento confirmado", desc: "A ativação não foi aprovada e devolvemos o valor como saldo na sua conta. Você pode tentar de novo pela carteira." };
    return { icon: <Clock className="h-12 w-12 text-amber-500" />, title: "Pagamento confirmado!", desc: "Ativação em processamento. Você será avisado no seu contato assim que concluir." };
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pix-checkout">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-3">
            {app.logo && <img src={app.logo} alt="" className="h-8 w-8 rounded object-cover" onError={(e) => (e.target.style.display = "none")} />}
            Pagar {app.name} via Pix
          </DialogTitle>
          <DialogDescription>Pague por Pix e ativamos automaticamente após a confirmação.</DialogDescription>
        </DialogHeader>

        {step === "form" && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pix-mac">{macLabel}</Label>
              <Input id="pix-mac" value={form.mac_app} onChange={onMacChange} maxLength={app.needs_device_id ? undefined : 17} placeholder={macPlaceholder} data-testid="pix-mac-input" />
            </div>
            {app.needs_mac_key && (
              <div className="space-y-2">
                <Label htmlFor="pix-key">Chave (KEY)</Label>
                <Input id="pix-key" value={form.key} onChange={set("key")} placeholder="Chave do aplicativo" data-testid="pix-key-input" />
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="pix-email">E-mail</Label>
                <Input id="pix-email" type="email" value={form.email} onChange={set("email")} placeholder="voce@email.com" data-testid="pix-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pix-contact">WhatsApp ou Telegram</Label>
                <Input id="pix-contact" value={form.contact} onChange={set("contact")} placeholder="+55 11 99999-9999" data-testid="pix-contact-input" />
              </div>
            </div>
            {pixProvider === "asaas" && (
              <div className="space-y-2">
                <Label htmlFor="pix-cpf">CPF do pagador</Label>
                <Input id="pix-cpf" value={form.cpf} onChange={set("cpf")} placeholder="000.000.000-00" maxLength={14} data-testid="pix-cpf-input" />
              </div>
            )}
            <div className="flex gap-2">
              <Input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Cupom de desconto" data-testid="pix-coupon-input" />
              <Button type="button" variant="outline" onClick={applyCoupon} data-testid="pix-coupon-apply">Aplicar</Button>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-border bg-muted/40 p-3 text-sm">
              <span className="text-muted-foreground">Total via Pix</span>
              <span className="font-num text-lg font-bold text-primary" data-testid="pix-total">
                {discount > 0 && <span className="mr-2 text-sm font-normal text-muted-foreground line-through">R$ {brl(app.price)}</span>}
                R$ {brl(discount > 0 ? Math.max(0, Number(app.price) - discount) : app.price)}
              </span>
            </div>
            {error && <p className="text-sm text-destructive" data-testid="pix-error">{error}</p>}
            <Button className="w-full gap-2 glow-red" onClick={createCharge} disabled={loading} data-testid="pix-generate-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
              {loading ? "Gerando Pix..." : "Gerar Pix"}
            </Button>
          </div>
        )}

        {step === "pay" && charge && (
          <div className="flex flex-col items-center gap-4">
            {charge.brcode_base64?.startsWith("data:image") ? (
              <img src={charge.brcode_base64} alt="QR Code Pix" className="h-52 w-52 rounded-lg border border-border bg-white p-2" data-testid="pix-qr-image" />
            ) : (
              <div className="flex h-52 w-52 items-center justify-center rounded-lg border border-border bg-muted"><QrCode className="h-16 w-16 text-muted-foreground" /></div>
            )}
            <div className="text-center">
              <div className="font-num text-2xl font-bold text-primary">R$ {brl(charge.amount)}</div>
              <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Aguardando pagamento...
              </p>
            </div>
            <div className="w-full space-y-2">
              <Label>Pix Copia e Cola</Label>
              <div className="flex items-center gap-2">
                <Input readOnly value={charge.brcode || ""} className="font-mono text-xs" data-testid="pix-brcode" />
                <Button variant="outline" size="icon" onClick={copyCode} data-testid="pix-copy-btn">
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <p className="text-center text-xs text-muted-foreground">
              Após o pagamento a ativação é disparada automaticamente. Pode fechar esta janela — avisaremos no seu contato.
            </p>
          </div>
        )}

        {step === "done" && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-4 py-6 text-center" data-testid="pix-done">
            {doneView().icon}
            <div>
              <h3 className="font-heading text-lg font-semibold">{doneView().title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{doneView().desc}</p>
            </div>
            <Button className="w-full" onClick={() => onOpenChange(false)} data-testid="pix-close-btn">Fechar</Button>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
  );
}
